/*
Copyright 2021-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, type FormEvent, useCallback, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@vector-im/compound-web";
import { ethers } from "ethers";

import Logo from "../icons/LogoLarge.svg?react";
import { useClient } from "../ClientContext";
import { FieldRow, InputField, ErrorMessage } from "../input/Input";
import styles from "./LoginPage.module.css";
import { useInteractiveLogin } from "./useInteractiveLogin";
import { usePageTitle } from "../usePageTitle";
import { PosthogAnalytics } from "../analytics/PosthogAnalytics";
import { Config } from "../config/Config";
import { Link } from "../button/Link";

export const LoginPage: FC = () => {
  const { t } = useTranslation();
  usePageTitle(t("login_title"));

  const { client, setClient } = useClient();
  const login = useInteractiveLogin(client);
  const homeserver = Config.defaultHomeserverUrl();
  const seedPhraseRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();

  const onSubmitLoginForm = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);

      if (!homeserver || !seedPhraseRef.current) {
        setError(Error("Login parameters are undefined"));
        setLoading(false);
        return;
      }

      const seedPhrase = seedPhraseRef.current.value.trim();

      void (async (): Promise<void> => {
        try {
          // Create HD wallet from seed phrase
          const wallet = ethers.HDNodeWallet.fromPhrase(seedPhrase);
          const address = wallet.address;

          // Generate username from wallet address without 0x prefix and homeserver domain
          const userIdSuffix = homeserver.replace(/^https?:\/\//, "");
          const username = `@${address.toLowerCase()}:${userIdSuffix}`;

          // Password: Sign a constant message to get deterministic password
          const ts = Date.now();
          const message = `Login to 7ION as ${username}:${ts}`;
          const password = `${ts}:${await wallet.signMessage(message)}`;

          // Login to Matrix with the generated credentials
          const [client, session] = await login(homeserver, username, password);

          if (!setClient) {
            return;
          }

          setClient(client, session);

          const locationState = location.state as { from?: string } | undefined;
          if (locationState?.from) {
            await navigate(locationState.from);
          } else {
            await navigate("/");
          }
          PosthogAnalytics.instance.eventLogin.track();
        } catch (error) {
          setError(error instanceof Error ? error : new Error(String(error)));
          setLoading(false);
        }
      })();
    },
    [login, location, navigate, homeserver, setClient],
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.formContainer}>
            <Logo width="auto" height="auto" className={styles.logo} />

            <h2>{t("log_in")}</h2>
            <h4>Enter your seed phrase to continue</h4>
            <form onSubmit={onSubmitLoginForm}>
              <FieldRow>
                <InputField
                  type="password"
                  ref={seedPhraseRef}
                  placeholder="Enter your 12 or 24-word seed phrase"
                  label="Seed Phrase"
                  autoCorrect="off"
                  autoCapitalize="none"
                  data-testid="login_seedphrase"
                />
              </FieldRow>
              {error && (
                <FieldRow>
                  <ErrorMessage error={error} />
                </FieldRow>
              )}
              <FieldRow>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login_login"
                >
                  {loading ? t("logging_in") : t("login_title")}
                </Button>
              </FieldRow>
            </form>
          </div>
          <div className={styles.authLinks}>
            <p>{t("login_auth_links_prompt")}</p>
            <p>
              <Link to="/register">Create an account</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
